import { Color, withCitrusTheme } from '@gousto-internal/citrus-react';
import { COMPARISON_TABLE_BREAKPOINT, COMPARISON_TABLE_RADIUS } from './tokens';

const DESKTOP = `@media (min-width: ${COMPARISON_TABLE_BREAKPOINT})`;

const GOUSTO_BORDER = 'rgba(14, 124, 67, 0.38)';
const GOUSTO_ROW_BORDER = 'rgba(14, 124, 67, 0.16)';
const COMPETITOR_ROW_BORDER = 'rgba(26, 32, 38, 0.08)';

export type ScrollerProps = React.JSX.IntrinsicElements['div'] & {
  gridArea?: string;
};

export const Scroller = withCitrusTheme<'div', ScrollerProps>(
  'div',
  (_theme, { gridArea }) => ({
    overflowX: 'auto',
    gridArea,
  }),
  { omitProps: ['gridArea'] },
);

export type TableProps = React.JSX.IntrinsicElements['table'] & {
  backgroundColor?: Color;
};

export const Table = withCitrusTheme<'table', TableProps>(
  'table',
  (theme, { backgroundColor }) => ({
    boxSizing: 'border-box',
    borderCollapse: 'separate',
    borderSpacing: `${theme.spacing(2)}px 0`,
    backgroundColor: theme.color(backgroundColor ?? Color.White),
    borderRadius: COMPARISON_TABLE_RADIUS,
    tableLayout: 'auto',
    width: '100%',
    minWidth: '340px',
    maxWidth: '535px',
    margin: '0 auto',
    color: theme.color(Color.ColdGrey_800),
    paddingLeft: 0,
    paddingRight: theme.spacing(0.5),
    paddingTop: '10px',
    paddingBottom: theme.spacing(3),

    '--value-size': '16px',
    '--value-size-gousto': '18px',

    '& col:first-of-type': { width: '26%' },
    '& col:not(:first-of-type)': { width: '22%' },

    '& td[data-highlighted="true"], & th[data-highlighted="true"]': {
      borderLeft: `1px solid ${GOUSTO_BORDER}`,
      borderRight: `1px solid ${GOUSTO_BORDER}`,
    },
    '& thead tr:first-of-type td[data-highlighted="true"]': {
      borderTop: `1px solid ${GOUSTO_BORDER}`,
      borderTopLeftRadius: COMPARISON_TABLE_RADIUS,
      borderTopRightRadius: COMPARISON_TABLE_RADIUS,
    },
    '& thead tr:last-of-type th:not([data-highlighted="true"])': {
      borderTopLeftRadius: COMPARISON_TABLE_RADIUS,
      borderTopRightRadius: COMPARISON_TABLE_RADIUS,
    },
    '& tbody tr:last-of-type td': {
      borderBottomLeftRadius: COMPARISON_TABLE_RADIUS,
      borderBottomRightRadius: COMPARISON_TABLE_RADIUS,
    },
    '& tbody tr:last-of-type td[data-highlighted="true"]': {
      borderBottom: `1px solid ${GOUSTO_BORDER}`,
    },
    '& tbody tr:first-of-type > *': { borderTop: 'none' },

    [DESKTOP]: {
      borderSpacing: `${theme.spacing(4)}px 0`,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(4),
      '--value-size': '24px',
      '--value-size-gousto': '28px',
    },
  }),
  { omitProps: ['backgroundColor'] },
);

export const HiddenCaption = withCitrusTheme('caption', () => ({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}));

export const PillBandCell = withCitrusTheme('td', (theme) => ({
  backgroundColor: theme.color(Color.Success_50),
  textAlign: 'center',
  verticalAlign: 'bottom',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(1),
}));

export const GoustoLogoWrapper = withCitrusTheme('span', () => ({
  display: 'inline-flex',
  marginTop: '10px',
  '& svg': { width: '65px', height: '20px' },
  [DESKTOP]: { '& svg': { width: '79px', height: '24px' } },
}));

export const BestValueBadge = withCitrusTheme('span', (theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.color(Color.Success_900),
  color: theme.color(Color.White),
  fontSize: '8px',
  fontWeight: 800,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  borderRadius: '999px',
  lineHeight: 'normal',
  letterSpacing: '0.24px',
  padding: `${theme.spacing(1)}px ${theme.spacing(1)}px`,
  [DESKTOP]: {
    fontSize: '10px',
    letterSpacing: '0.84px',
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
  },
}));

export const HeaderCell = withCitrusTheme('th', (theme) => ({
  textAlign: 'center',
  verticalAlign: 'middle',
  padding: `${theme.spacing(2)}px ${theme.spacing(1)}px ${theme.spacing(3)}px`,
  fontWeight: 700,
  ...theme.tokens.fontSizes[0],
  backgroundColor: theme.color(Color.White),
  color: theme.color(Color.ColdGrey_800),
  '&[data-highlighted="true"]': {
    backgroundColor: theme.color(Color.Success_50),
    color: theme.color(Color.Primary_900),
  },
  [DESKTOP]: { ...theme.tokens.fontSizes[3] },
}));

export const RowHeaderCell = withCitrusTheme('th', (theme) => ({
  textAlign: 'left',
  verticalAlign: 'middle',
  padding: `${theme.spacing(3)}px ${theme.spacing(1)}px ${theme.spacing(3)}px 0`,
  fontWeight: 700,
  ...theme.tokens.fontSizes[3],
  color: theme.color(Color.ColdGrey_800),
}));

export const DataCell = withCitrusTheme('td', (theme) => ({
  textAlign: 'center',
  verticalAlign: 'middle',
  padding: `${theme.spacing(3)}px ${theme.spacing(1)}px`,
  backgroundColor: theme.color(Color.White),
  color: theme.color(Color.ColdGrey_800),
  borderTop: `1px solid ${COMPETITOR_ROW_BORDER}`,
  '&[data-highlighted="true"]': {
    backgroundColor: theme.color(Color.Success_50),
    borderTop: `1px solid ${GOUSTO_ROW_BORDER}`,
  },
}));

export const FromCaption = withCitrusTheme('span', (theme) => ({
  display: 'block',
  ...theme.tokens.fontSizes[0],
  color: theme.color(Color.ColdGrey_600),
}));

export const ValueText = withCitrusTheme('span', (theme) => ({
  display: 'block',
  fontWeight: 800,
  letterSpacing: '-0.02em',
  fontSize: 'var(--value-size)',
  color: theme.color(Color.ColdGrey_800),
  '&[data-highlighted="true"]': {
    color: theme.color(Color.Success_900),
    fontSize: 'var(--value-size-gousto)',
  },
}));

export const AvailabilityStack = withCitrusTheme('span', (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const AvailabilityLabel = withCitrusTheme('span', (theme) => ({
  fontSize: '12px',
  color: theme.color(Color.ColdGrey_800),
  [DESKTOP]: { fontSize: '14px' },
}));
